import { StatusBadge } from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { Download01, Eye } from "@untitledui/icons";
import { useState } from "react";
import { type Action, type Column, TableCustom } from "@/components/ui/table";
import { veiculosBuscar, veiculosInfo } from "@/services/analytics/events";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import { useExportVehicles } from "@hooks";
import { REPORT_TYPE } from "@/shared/types/generate-report-request";
import { TruncatedText } from "@/shared/components/truncated-text";
import { VehicleFormModal, VehiclesExportModal } from "../components";
import { VEHICLE_STATUS_BADGE_PALETTES } from "../constants/vehicle-status.constant";
import { vehicleSituationLabel } from "../handlers";
import { useGetVehicles } from "../query";
import type { Vehicle } from "../types";

const GROUP_CELL_MAX_WIDTH_PX = 300;
const SEARCH_DEBOUNCE_MS = 500;
const ITEMS_PER_PAGE = 10;

export default function VehiclesList() {
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [exportMessage, setExportMessage] = useState<string | null>(null);
	const exportMutation = useExportVehicles({
		onSuccess: (response) => {
			setExportMessage(response.message);
			setIsExportModalOpen(true);
		},
	});

	const vehiclesQuery = useGetVehicles({
		page: currentPage,
		pageSize: ITEMS_PER_PAGE,
		search: searchTerm,
	});
	const { data: vehiclesData, isFetching, isError, refetch } = vehiclesQuery;

	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria: { search: searchTerm, pageSize: ITEMS_PER_PAGE },
		totalPages: vehiclesData?.totalPages,
		query: vehiclesQuery,
	});

	useDebouncedCommit({
		value: searchInput,
		applied: searchTerm,
		delay: SEARCH_DEBOUNCE_MS,
		onCommit: (term) => {
			setSearchTerm(term);
			setCurrentPage(1);
			if (term) {
				veiculosBuscar(term);
			}
		},
	});

	const columns: Column<Vehicle>[] = [
		{
			id: "plate",
			label: "Placa",
			render: (vehicle) => vehicle.plate,
		},
		{
			id: "model",
			label: "Modelo",
			render: (vehicle) => vehicle.model,
		},
		{
			id: "odometer",
			label: "Hodômetro",
			render: (vehicle) =>
				vehicle.odometer
					? `${vehicle.odometer.toLocaleString("pt-BR")} km`
					: "-",
		},
		{
			id: "group",
			label: "Grupo",
			render: (vehicle) => (
				<TruncatedText
					maxWidth={GROUP_CELL_MAX_WIDTH_PX}
					text={vehicle.group?.name}
				/>
			),
		},
		{
			id: "status",
			label: "Status",
			render: (vehicle) =>
				vehicle.status ? (
					<StatusBadge
						label={vehicleSituationLabel(vehicle.status)}
						palette={VEHICLE_STATUS_BADGE_PALETTES[vehicle.status]}
					/>
				) : null,
		},
	];

	const actions: Action<Vehicle>[] = [
		{
			label: "Detalhes do veículo",
			icon: Eye,
			onClick: (vehicle) => {
				veiculosInfo(vehicle.id);
				window.open(
					`/vehicles/?id=${vehicle.id}`,
					"_blank",
					"noopener,noreferrer"
				);
			},
		},
	];

	const handleAddVehicleSuccess = () => {
		setCurrentPage(1);
		setSearchInput("");
		setSearchTerm("");
	};

	return (
		<>
			<TableCustom
				actions={actions}
				addButtonLabel="Novo veículo"
				columns={columns}
				currentPage={currentPage}
				data={vehiclesData?.items || []}
				errorMessage="Erro ao carregar veículos. Tente novamente."
				extraActions={
					<Button
						aria-label="Exportar veículos"
						color="secondary"
						iconLeading={Download01}
						isLoading={exportMutation.isPending}
						onClick={() =>
							exportMutation.mutate({ type: REPORT_TYPE.Vehicles })
						}
						size="sm"
						type="button"
					>
						Exportar
					</Button>
				}
				filterBarLabel="Filtros de veículos"
				isError={isError}
				isLoading={isFetching}
				layout="page"
				onAdd={() => setIsAddModalOpen(true)}
				onPageChange={setCurrentPage}
				onRetry={() => {
					refetch();
				}}
				onSearchChange={(value) => {
					setSearchInput(value);
					setCurrentPage(1);
				}}
				searchPlaceholder="Pesquise pela placa"
				searchValue={searchInput}
				title="Lista de veículos"
				totalPages={totalPages}
			/>

			<VehicleFormModal
				isOpen={isAddModalOpen}
				mode="create"
				onClose={() => setIsAddModalOpen(false)}
				onSuccess={handleAddVehicleSuccess}
			/>

			<VehiclesExportModal
				isOpen={isExportModalOpen}
				message={exportMessage}
				onClose={() => setIsExportModalOpen(false)}
			/>
		</>
	);
}
